<template>
  <div class="user-prompts">
    <!-- Prompt library: the persisting set of prompts admins maintain -->
    <el-card>
      <div slot="header" class="card-header">
        <span>{{ $t('userPrompts.libraryTitle') }}</span>
      </div>

      <div class="add-row">
        <el-button type="primary" icon="el-icon-plus" class="create-prompt-btn" @click="openCreateDialog">{{ $t('userPrompts.createPrompt') }}</el-button>
      </div>

      <el-table
        class="prompt-table"
        :data="prompts"
        border
        stripe
        highlight-current-row
        v-loading="loadingPrompts"
        @current-change="handlePromptSelect"
      >
        <el-table-column width="50" align="center">
          <template slot-scope="scope">
            <el-radio :value="selectedPrompt && selectedPrompt.id" :label="scope.row.id" @change="handlePromptSelect(scope.row)">
              <span></span>
            </el-radio>
          </template>
        </el-table-column>
        <el-table-column prop="id" :label="$t('userPrompts.colId')" width="70"></el-table-column>
        <!-- English | Chinese, equal halves of the flexible width. Text wraps rather than
             truncating, so the two editions can be compared line by line. -->
        <el-table-column :label="$t('userPrompts.colContentEn')" min-width="240" class-name="col-content-en">
          <template slot-scope="scope">
            <editable-text
              :value="scope.row.content"
              required
              :placeholder="$t('userPrompts.englishPlaceholder')"
              :save="text => savePromptField(scope.row, 'content', text)"
            ></editable-text>
          </template>
        </el-table-column>
        <el-table-column :label="$t('userPrompts.colContentCn')" min-width="240" class-name="col-content-cn">
          <template slot-scope="scope">
            <editable-text
              :value="scope.row.cn_content || ''"
              :placeholder="$t('userPrompts.chinesePlaceholder')"
              :empty-text="$t('userPrompts.addChinese')"
              :save="text => savePromptField(scope.row, 'cn_content', text)"
            ></editable-text>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" :label="$t('userPrompts.colCreatedAt')" width="180"></el-table-column>
        <!-- Deliberately not fixed="right". A fixed column makes Element UI render a hidden
             duplicate of every row, including a second EditableText per cell that never
             enters edit mode, so while one cell is being edited its row grows and the
             pinned column's copy does not: the Delete buttons drift out of line. -->
        <el-table-column :label="$t('userPrompts.colAction')" width="100">
          <template slot-scope="scope">
            <el-button type="text" style="color: #F56C6C;" @click="handleDelete(scope.row)">{{ $t('userPrompts.delete') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Prompt Scheduling: the prompt currently active for everyone, plus the default fallback -->
    <el-card class="assign-card">
      <div slot="header" class="card-header">
        <span>{{ $t('userPrompts.scheduleTitle') }}</span>
        <span>
          <el-button size="mini" @click="handleSchedule">{{ $t('userPrompts.schedule') }}</el-button>
          <el-button
            type="primary"
            size="mini"
            :disabled="!selectedPrompt"
            :loading="settingCurrent"
            @click="handleSetCurrent"
          >{{ $t('userPrompts.setCurrent') }}</el-button>
        </span>
      </div>

      <el-table :data="scheduleRows" border stripe v-loading="loadingSchedule">
        <el-table-column prop="content" :label="$t('userPrompts.colCurrent')" show-overflow-tooltip></el-table-column>
        <el-table-column :label="$t('userPrompts.colActiveSince')" width="220">
          <template slot-scope="scope">
            <span>{{ scope.row.time || '------' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('userPrompts.colAction')" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.type === 'scheduled'"
              type="text"
              style="color: #F56C6C;"
              @click="handleDeleteSchedule(scope.row)"
            >{{ $t('userPrompts.delete') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Create dialog: English is required, Chinese optional (zh users fall back to English) -->
    <el-dialog
      :title="$t('userPrompts.createDialogTitle')"
      :visible.sync="createDialogVisible"
      width="560px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="create-prompt-dialog"
    >
      <el-form label-position="top">
        <el-form-item :label="$t('userPrompts.labelEnglish')" required>
          <el-input
            v-model="createForm.content"
            class="create-input-en"
            type="textarea"
            :rows="3"
            maxlength="500"
            show-word-limit
            :placeholder="$t('userPrompts.englishPlaceholder')"
          ></el-input>
        </el-form-item>
        <el-form-item :label="$t('userPrompts.labelChinese')">
          <el-input
            v-model="createForm.cn_content"
            class="create-input-cn"
            type="textarea"
            :rows="3"
            maxlength="500"
            show-word-limit
            :placeholder="$t('userPrompts.chinesePlaceholder')"
          ></el-input>
          <div class="form-tip">{{ $t('userPrompts.chineseOptionalTip') }}</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" class="create-confirm-btn" :loading="adding" @click="handleAdd">{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>

    <!-- Schedule dialog: pick a future time to make the selected prompt current -->
    <el-dialog
      :title="$t('userPrompts.scheduleDialogTitle')"
      :visible.sync="scheduleDialogVisible"
      width="420px"
      append-to-body
    >
      <p class="dialog-hint">
        {{ $t('userPrompts.selectedPrompt') }}:
        <strong>{{ selectedPrompt ? selectedPrompt.content : $t('userPrompts.none') }}</strong>
      </p>
      <el-date-picker
        v-model="scheduleTime"
        type="datetime"
        :placeholder="$t('userPrompts.scheduleTimePlaceholder')"
        value-format="yyyy-MM-dd HH:mm:ss"
        :picker-options="schedulePickerOptions"
        style="width: 100%;"
      ></el-date-picker>
      <span slot="footer">
        <el-button @click="scheduleDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="scheduling" @click="confirmSchedule">{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getPrompts, createPrompt, updatePrompt, deletePrompt, getCurrentScheduling, setCurrentPrompt, schedulePrompt, cancelSchedule } from '@/api/userPrompts'
import EditableText from './EditableText.vue'

export default {
  name: 'UserPrompts',
  components: { EditableText },
  data() {
    return {
      prompts: [],
      loadingPrompts: false,
      selectedPrompt: null,

      // Create dialog
      createDialogVisible: false,
      createForm: { content: '', cn_content: '' },
      adding: false,

      // Prompt Scheduling: { current: {id, content, active_since} | null, default: {id, content} | null }
      currentSchedule: { current: null, default: null, scheduled: [] },
      loadingSchedule: false,
      settingCurrent: false,

      // Schedule dialog
      scheduleDialogVisible: false,
      scheduleTime: null,
      scheduling: false,
      schedulePickerOptions: {
        // Disallow days before today; the backend also rejects past times.
        disabledDate(date) {
          return date.getTime() < Date.now() - 24 * 60 * 60 * 1000
        }
      }
    }
  },
  computed: {
    // Rows for the scheduling table: active prompt (if set), the default, then any
    // pending scheduled activations. Each row carries a `type` used to gate the delete action.
    scheduleRows() {
      const rows = []
      const cur = this.currentSchedule.current
      const def = this.currentSchedule.default
      const scheduled = this.currentSchedule.scheduled || []
      if (cur) {
        rows.push({ type: 'current', id: cur.id, content: cur.content, time: cur.active_since })
      }
      if (def && (!cur || def.id !== cur.id)) {
        rows.push({ type: 'default', id: def.id, content: def.content + ' ' + this.$t('userPrompts.defaultTag'), time: null })
      }
      scheduled.forEach(s => {
        rows.push({ type: 'scheduled', scheduleId: s.id, content: s.content + ' ' + this.$t('userPrompts.scheduledTag'), time: s.scheduled_at })
      })
      return rows
    }
  },
  created() {
    this.loadPrompts()
    this.loadCurrentScheduling()
  },
  methods: {
    // Prompt library
    loadPrompts() {
      this.loadingPrompts = true
      getPrompts().then(res => {
        this.prompts = res.data || []
      }).catch(() => {
        this.$message.error(this.$t('userPrompts.loadFail'))
      }).finally(() => {
        this.loadingPrompts = false
      })
    },
    openCreateDialog() {
      this.createForm = { content: '', cn_content: '' }
      this.createDialogVisible = true
    },
    handleAdd() {
      const content = (this.createForm.content || '').trim()
      const cnContent = (this.createForm.cn_content || '').trim()
      if (!content) {
        this.$message.warning(this.$t('userPrompts.emptyPrompt'))
        return
      }
      this.adding = true
      createPrompt({ content, cn_content: cnContent }).then(() => {
        this.$message.success(this.$t('userPrompts.addSuccess'))
        this.createDialogVisible = false
        this.loadPrompts()
      }).catch(() => {
        this.$message.error(this.$t('userPrompts.addFail'))
      }).finally(() => {
        this.adding = false
      })
    },
    // Save one edited cell. Only that field is sent, so the other language column is never
    // overwritten. Returns the promise so EditableText stays open if the save fails.
    savePromptField(row, field, text) {
      return updatePrompt(row.id, { [field]: text }).then(res => {
        const saved = (res && res.data) || {}
        // Take the server's value: it trims, and stores a cleared Chinese text as null
        row[field] = Object.prototype.hasOwnProperty.call(saved, field) ? saved[field] : text
        this.$message.success(this.$t('userPrompts.editSuccess'))
        // The scheduling card shows the English text of the current/default prompt
        if (field === 'content') {
          this.loadCurrentScheduling()
        }
      })
    },
    handlePromptSelect(row) {
      this.selectedPrompt = row
    },
    async handleDelete(row) {
      try {
        await this.$confirm(this.$t('userPrompts.deleteConfirm'), this.$t('common.tip'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        deletePrompt(row.id).then(() => {
          this.$message.success(this.$t('userPrompts.deleteSuccess'))
          if (this.selectedPrompt && this.selectedPrompt.id === row.id) {
            this.selectedPrompt = null
          }
          this.loadPrompts()
        }).catch(() => {
          this.$message.error(this.$t('userPrompts.deleteFail'))
        })
      } catch (e) {
        // cancelled
      }
    },

    // Prompt Scheduling
    loadCurrentScheduling() {
      this.loadingSchedule = true
      getCurrentScheduling().then(res => {
        this.currentSchedule = res.data || { current: null, default: null, scheduled: [] }
      }).catch(() => {
        this.$message.error(this.$t('userPrompts.loadFail'))
      }).finally(() => {
        this.loadingSchedule = false
      })
    },
    // Open the schedule dialog (requires a prompt selected in the library).
    handleSchedule() {
      if (!this.selectedPrompt) {
        this.$message.warning(this.$t('userPrompts.scheduleNoPrompt'))
        return
      }
      this.scheduleTime = null
      this.scheduleDialogVisible = true
    },
    confirmSchedule() {
      if (!this.selectedPrompt) {
        this.$message.warning(this.$t('userPrompts.scheduleNoPrompt'))
        return
      }
      if (!this.scheduleTime) {
        this.$message.warning(this.$t('userPrompts.scheduleNoTime'))
        return
      }
      this.scheduling = true
      schedulePrompt({ prompt_id: this.selectedPrompt.id, scheduled_at: this.scheduleTime }).then(() => {
        this.$message.success(this.$t('userPrompts.scheduleSuccess'))
        this.scheduleDialogVisible = false
        this.loadCurrentScheduling()
      }).catch(() => {
        this.$message.error(this.$t('userPrompts.scheduleFail'))
      }).finally(() => {
        this.scheduling = false
      })
    },
    async handleDeleteSchedule(row) {
      try {
        await this.$confirm(this.$t('userPrompts.deleteScheduleConfirm'), this.$t('common.tip'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        cancelSchedule(row.scheduleId).then(() => {
          this.$message.success(this.$t('userPrompts.deleteScheduleSuccess'))
          this.loadCurrentScheduling()
        }).catch(() => {
          this.$message.error(this.$t('userPrompts.deleteScheduleFail'))
        })
      } catch (e) {
        // cancelled
      }
    },
    // Set the prompt selected in the library as the current one for everyone.
    handleSetCurrent() {
      if (!this.selectedPrompt) {
        this.$message.warning(this.$t('userPrompts.setCurrentNoPrompt'))
        return
      }
      this.settingCurrent = true
      setCurrentPrompt({ prompt_id: this.selectedPrompt.id }).then(() => {
        this.$message.success(this.$t('userPrompts.setCurrentSuccess'))
        this.loadCurrentScheduling()
      }).catch(() => {
        this.$message.error(this.$t('userPrompts.setCurrentFail'))
      }).finally(() => {
        this.settingCurrent = false
      })
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 16px;
}
.selected-hint {
  font-weight: normal;
  font-size: 13px;
  color: #909399;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.add-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}
/* A divider between the English and Chinese halves */
.prompt-table >>> td.col-content-en,
.prompt-table >>> th.col-content-en {
  border-right: 2px solid #dcdfe6;
}
.form-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.assign-card {
  margin-top: 20px;
}
.dialog-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #909399;
}
</style>
